import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase/config';

class SubscriptionService {
  /**
   * Check if user has an active subscription
   */
  async checkSubscriptionStatus(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        return {
          isSubscribed: false,
          isUnlimited: false,
          subscriptionType: 'none',
          maxAccounts: 0,
          maxVideosPerMonth: 0,
          videosUsedThisMonth: 0,
          twitterUsernames: []
        };
      }

      const userData = userDoc.data();
      
      // PRIORITY CHECK: If user is unlimited, return unlimited status immediately
      if (userData.isUnlimited) {
        return {
          isSubscribed: true,
          isUnlimited: true,
          subscriptionType: 'unlimited',
          maxAccounts: 999,
          maxVideosPerMonth: -1, // Unlimited
          videosUsedThisMonth: userData.videosUsedThisMonth || 0,
          twitterUsernames: userData.twitterUsernames || [],
          subscriptionPrice: userData.subscriptionPrice || 0,
          subscriptionStartDate: userData.subscriptionStartDate
        };
      }
      
      // Check if it's a new month and reset video count (only for non-unlimited users)
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      let lastResetDate = userData.currentPeriodStart?.toDate();
      if (!lastResetDate) {
        lastResetDate = new Date(0); // Epoch date if not set
      }
      
      const lastResetMonth = lastResetDate.getMonth();
      const lastResetYear = lastResetDate.getFullYear();
      
      // Reset video count if it's a new month
      if (currentMonth !== lastResetMonth || currentYear !== lastResetYear) {
        await updateDoc(doc(db, 'users', userId), {
          videosUsedThisMonth: 0,
          currentPeriodStart: currentDate
        });
        userData.videosUsedThisMonth = 0;
      }

      return {
        isSubscribed: userData.isSubscribed || false,
        isUnlimited: false,
        subscriptionType: userData.subscriptionType || 'none',
        maxAccounts: userData.maxAccounts || 0,
        maxVideosPerMonth: userData.maxVideosPerMonth || 0,
        videosUsedThisMonth: userData.videosUsedThisMonth || 0,
        twitterUsernames: userData.twitterUsernames || [],
        subscriptionPrice: userData.subscriptionPrice || 0,
        subscriptionStartDate: userData.subscriptionStartDate
      };
    } catch (error) {
      console.error('Error checking subscription status:', error);
      throw error;
    }
  }

  /**
   * Check if user can generate a video (respects limits)
   */
  async canGenerateVideo(userId) {
    try {
      const subscription = await this.checkSubscriptionStatus(userId);
      
      // PRIORITY CHECK: Unlimited users can always generate videos
      if (subscription.isUnlimited) {
        return {
          canGenerate: true,
          subscription
        };
      }
      
      if (!subscription.isSubscribed) {
        return {
          canGenerate: false,
          reason: 'No active subscription'
        };
      }

      // Unlimited plans or non-free plans have no video limits
      if (subscription.subscriptionType !== 'free') {
        return {
          canGenerate: true,
          subscription
        };
      }

      // Check free plan limits
      if (subscription.maxVideosPerMonth && 
          subscription.videosUsedThisMonth >= subscription.maxVideosPerMonth) {
        return {
          canGenerate: false,
          reason: 'Monthly video limit reached',
          videosUsed: subscription.videosUsedThisMonth,
          videoLimit: subscription.maxVideosPerMonth
        };
      }

      return {
        canGenerate: true,
        subscription
      };
    } catch (error) {
      console.error('Error checking video generation limits:', error);
      throw error;
    }
  }

  /**
   * Increment video usage count
   */
  async incrementVideoUsage(userId) {
    try {
      const subscription = await this.checkSubscriptionStatus(userId);
      
      // PRIORITY CHECK: Never increment usage for unlimited users
      if (subscription.isUnlimited) {
        return; // Skip incrementing for unlimited users
      }
      
      // Only increment for free plans
      if (subscription.subscriptionType === 'free' && subscription.maxVideosPerMonth) {
        await updateDoc(doc(db, 'users', userId), {
          videosUsedThisMonth: increment(1)
        });
      }
    } catch (error) {
      console.error('Error incrementing video usage:', error);
      throw error;
    }
  }

  /**
   * Check if user can generate multiple videos (bulk generation)
   */
  async canGenerateBulkVideos(userId, videoCount) {
    try {
      const subscription = await this.checkSubscriptionStatus(userId);
      
      // PRIORITY CHECK: Unlimited users can generate any amount
      if (subscription.isUnlimited) {
        return {
          canGenerate: true,
          subscription
        };
      }
      
      if (!subscription.isSubscribed) {
        return {
          canGenerate: false,
          reason: 'No active subscription'
        };
      }

      // Non-free plans have no video limits
      if (subscription.subscriptionType !== 'free') {
        return {
          canGenerate: true,
          subscription
        };
      }

      // Check free plan limits for bulk generation
      if (subscription.maxVideosPerMonth) {
        const videosUsed = subscription.videosUsedThisMonth || 0;
        const videosRemaining = subscription.maxVideosPerMonth - videosUsed;
        
        if (videoCount > videosRemaining) {
          return {
            canGenerate: false,
            reason: `Cannot generate ${videoCount} videos. You have ${videosRemaining} videos remaining this month.`,
            videosUsed,
            videosRemaining,
            videoLimit: subscription.maxVideosPerMonth,
            excessCount: videoCount - videosRemaining
          };
        }
      }

      return {
        canGenerate: true,
        subscription
      };
    } catch (error) {
      console.error('Error checking bulk video generation limits:', error);
      throw error;
    }
  }

  /**
   * Check if user can add more Twitter accounts
   */
  async canAddTwitterAccount(userId, newAccountsCount = 1) {
    try {
      const subscription = await this.checkSubscriptionStatus(userId);
      
      // PRIORITY CHECK: Unlimited users can add unlimited accounts
      if (subscription.isUnlimited) {
        return {
          canAdd: true,
          subscription
        };
      }
      
      if (!subscription.isSubscribed) {
        return {
          canAdd: false,
          reason: 'No active subscription'
        };
      }

      const currentAccounts = subscription.twitterUsernames.length;
      const totalAfterAdd = currentAccounts + newAccountsCount;
      
      if (totalAfterAdd > subscription.maxAccounts) {
        return {
          canAdd: false,
          reason: `Account limit reached. Current: ${currentAccounts}/${subscription.maxAccounts}`,
          currentAccounts,
          maxAccounts: subscription.maxAccounts
        };
      }

      return {
        canAdd: true,
        subscription
      };
    } catch (error) {
      console.error('Error checking Twitter account limits:', error);
      throw error;
    }
  }

  /**
   * Add Twitter account to user's allowed list
   */
  async addTwitterAccount(userId, username) {
    try {
      const subscription = await this.checkSubscriptionStatus(userId);
      
      // PRIORITY CHECK: Unlimited users can add any account
      if (subscription.isUnlimited) {
        const cleanUsername = username.toLowerCase();
        const currentUsernames = subscription.twitterUsernames || [];
        
        if (!currentUsernames.includes(cleanUsername)) {
          await updateDoc(doc(db, 'users', userId), {
            twitterUsernames: [...currentUsernames, cleanUsername]
          });
        }
        return { success: true };
      }
      
      const canAdd = await this.canAddTwitterAccount(userId);
      
      if (!canAdd.canAdd) {
        return {
          success: false,
          reason: canAdd.reason
        };
      }

      const cleanUsername = username.toLowerCase();
      const currentUsernames = subscription.twitterUsernames || [];
      
      if (currentUsernames.includes(cleanUsername)) {
        return {
          success: false,
          reason: 'Username already added'
        };
      }

      await updateDoc(doc(db, 'users', userId), {
        twitterUsernames: [...currentUsernames, cleanUsername]
      });

      return { success: true };
    } catch (error) {
      console.error('Error adding Twitter account:', error);
      throw error;
    }
  }

  /**
   * Remove Twitter account from user's list
   */
  async removeTwitterAccount(userId, username) {
    try {
      const subscription = await this.checkSubscriptionStatus(userId);
      const cleanUsername = username.toLowerCase();
      const currentUsernames = subscription.twitterUsernames || [];
      
      const updatedUsernames = currentUsernames.filter(u => u !== cleanUsername);
      
      await updateDoc(doc(db, 'users', userId), {
        twitterUsernames: updatedUsernames
      });

      return { success: true };
    } catch (error) {
      console.error('Error removing Twitter account:', error);
      throw error;
    }
  }

  /**
   * Get subscription display information
   */
  getSubscriptionDisplayInfo(subscription) {
    // PRIORITY CHECK: Handle unlimited users first
    if (subscription.isUnlimited) {
      return {
        title: 'Unlimited Plan',
        description: 'Unlimited videos and accounts',
        status: 'unlimited'
      };
    }
    
    if (!subscription.isSubscribed) {
      return {
        title: 'No Subscription',
        description: 'Sign up for a plan to start creating videos',
        status: 'inactive'
      };
    }

    if (subscription.subscriptionType === 'free') {
      const remaining = subscription.maxVideosPerMonth - subscription.videosUsedThisMonth;
      return {
        title: 'Free Plan',
        description: `${remaining} videos remaining this month`,
        status: 'free',
        videosUsed: subscription.videosUsedThisMonth,
        videoLimit: subscription.maxVideosPerMonth
      };
    }

    return {
      title: 'Pro Plan',
      description: `Up to ${subscription.maxAccounts} accounts`,
      status: 'pro',
      accounts: subscription.twitterUsernames.length,
      maxAccounts: subscription.maxAccounts,
      price: subscription.subscriptionPrice
    };
  }
}

export default new SubscriptionService(); 